/**
 * Database Analytics Service
 *
 * Implements analytics tracking using Firestore database
 * for reliable, scalable analytics storage
 */

import { insertDocument, queryDocuments } from './firestore'
import { GameEvents } from './gameAnalytics'
import { WordLength } from '../types/game'

// Collection name for analytics events
const EVENTS_COLLECTION = 'analytics_events'

// Anonymous user ID management
const getUserId = (): string => {
  let userId = localStorage.getItem('anonymous_user_id')

  if (!userId) {
    // Generate a random ID if none exists
    userId = `user_${Math.random().toString(36).substring(2, 15)}_${Date.now()}`
    localStorage.setItem('anonymous_user_id', userId)
  }

  return userId
}

// Analytics queue for batching and offline support
interface QueuedEvent {
  eventName: string
  eventParams: Record<string, string | number | boolean | null>
  timestamp: number
}

const eventQueue: QueuedEvent[] = []
let isProcessingQueue = false

// Process any queued events
const processEventQueue = async () => {
  if (isProcessingQueue || eventQueue.length === 0) return

  isProcessingQueue = true

  try {
    // Take a batch of events to process (up to 10)
    const batch = eventQueue.splice(0, 10)

    // Process each event in the batch
    const promises = batch.map((event) => {
      return trackEventToFirestore(event.eventName, event.eventParams)
    })

    await Promise.allSettled(promises)

    // If more events exist in the queue, process them
    if (eventQueue.length > 0) {
      setTimeout(processEventQueue, 100)
    }
  } catch (error) {
    console.error('Error processing event queue:', error)
  } finally {
    isProcessingQueue = false
  }
}

/**
 * Track a custom event to Firestore
 *
 * @param eventName - The name of the event to track
 * @param eventParams - Additional parameters to track with the event
 */
export const trackEventToFirestore = async (
  eventName: string,
  eventParams: Record<string, string | number | boolean | null> = {}
) => {
  const userId = getUserId()

  // Prepare the event data for Firestore insertion
  const eventData = {
    user_id: userId,
    event_name: eventName,
    event_params: eventParams,
    timestamp: Date.now(),
    created_at: new Date().toISOString(),
  }

  try {
    await insertDocument(EVENTS_COLLECTION, eventData)
    return true
  } catch (error) {
    console.error(`Error tracking event ${eventName} to Firestore:`, error)

    // Queue for retry if failed
    eventQueue.push({
      eventName,
      eventParams,
      timestamp: Date.now(),
    })

    return false
  }
}

/**
 * Track when a game is started
 */
export const trackGameStart = (wordLength: WordLength) => {
  trackEventToFirestore(GameEvents.GAME_STARTED, {
    word_length: wordLength,
    timestamp: Date.now(),
  })
}

/**
 * Track when a game is completed (won or lost)
 */
export const trackGameCompletion = (
  wordLength: WordLength,
  won: boolean,
  attempts: number,
  timeTaken: number
) => {
  trackEventToFirestore(GameEvents.GAME_COMPLETED, {
    word_length: wordLength,
    won: won,
    attempts: attempts,
    time_taken_seconds: Math.floor(timeTaken / 1000),
    timestamp: Date.now(),
  })
}

/**
 * Track when a player abandons a game
 */
export const trackGameAbandoned = (
  wordLength: WordLength,
  attemptsBeforeAbandoning: number
) => {
  trackEventToFirestore(GameEvents.GAME_ABANDONED, {
    word_length: wordLength,
    attempts_before_abandoning: attemptsBeforeAbandoning,
    timestamp: Date.now(),
  })
}

/**
 * Track when a guess is submitted
 */
export const trackGuessSubmitted = (
  wordLength: WordLength,
  guessNumber: number,
  correct: boolean
) => {
  trackEventToFirestore(GameEvents.GUESS_SUBMITTED, {
    word_length: wordLength,
    guess_number: guessNumber,
    correct: correct,
    timestamp: Date.now(),
  })
}

/**
 * Track when a user selects a game mode
 */
export const trackModeSelected = (wordLength: WordLength) => {
  trackEventToFirestore(GameEvents.MODE_SELECTED, {
    word_length: wordLength,
    timestamp: Date.now(),
  })
}

/**
 * Track when a user shares their result
 */
export const trackResultShared = (
  wordLength: WordLength,
  won: boolean,
  attempts: number
) => {
  trackEventToFirestore(GameEvents.RESULT_SHARED, {
    word_length: wordLength,
    won: won,
    attempts: attempts,
    timestamp: Date.now(),
  })
}

/**
 * Track when a user switches to another challenge
 */
export const trackChallengeSwitch = (
  fromWordLength: WordLength,
  toWordLength: WordLength
) => {
  trackEventToFirestore(GameEvents.SWITCH_CHALLENGE, {
    from_word_length: fromWordLength,
    to_word_length: toWordLength,
    timestamp: Date.now(),
  })
}

/**
 * Track when sharing is abandoned
 */
export const trackShareAbandoned = (
  wordLength: WordLength,
  won: boolean,
  attempts: number
) => {
  trackEventToFirestore(GameEvents.SHARE_ABANDONED, {
    word_length: wordLength,
    won: won,
    attempts: attempts,
    timestamp: Date.now(),
  })
}

/**
 * Track when a user encounters an error
 */
export const trackErrorEncountered = (
  errorType: string,
  errorMessage: string,
  wordLength?: WordLength
) => {
  trackEventToFirestore(GameEvents.ERROR_ENCOUNTERED, {
    error_type: errorType,
    error_message: errorMessage,
    word_length: wordLength || null,
    timestamp: Date.now(),
  })
}

/**
 * Get user analytics data for a specific event type
 *
 * @param eventName - The name of the event to query
 * @param limit - Maximum number of events to return
 * @returns Query results containing events
 */
export const getUserAnalytics = async (
  eventName: string,
  limit: number = 100
) => {
  const userId = getUserId()

  const results = await queryDocuments(
    EVENTS_COLLECTION,
    [
      ['user_id', '==', userId],
      ['event_name', '==', eventName],
    ],
    limit,
    'timestamp',
    'desc'
  )

  return results
}

/**
 * Initialize the database analytics system
 * - Sets up periodic queue processing
 * - Handles offline/online transitions
 */
export const initDatabaseAnalytics = () => {
  // Process any queued events immediately
  processEventQueue()

  // Set up periodic queue processing
  setInterval(processEventQueue, 60000) // Every minute

  // Handle online status changes
  window.addEventListener('online', () => {
    processEventQueue()
  })
}

export default {
  trackGameStart,
  trackGameCompletion,
  trackGameAbandoned,
  trackGuessSubmitted,
  trackModeSelected,
  trackResultShared,
  trackChallengeSwitch,
  trackShareAbandoned,
  trackErrorEncountered,
  getUserAnalytics,
  trackEventToFirestore,
  initDatabaseAnalytics,
}
